/**
 * GeoEvent expression to Arcade conversion helpers shared by mapping and generation.
 */

export interface ArcadeConversionAssessment {
  expression: string;
  autoConverted: boolean;
  reason?: string;
  unsupportedFunctions: string[];
  untrainedFunctions: string[];
}

/**
 * Functions known to be unsupported in the Velocity Arcade profile.
 * Sourced from ArcadeValidationUtil.scala in arcgis-velocity-services.
 */
const ARCADE_UNSUPPORTED_FUNCTIONS = new Set([
  'featureinfilter',
  'getenvironment',
  'isselfintersecting',
  'schema',
]);

/**
 * Arcade functions that this converter intentionally emits or preserves.
 * Unknown function calls are treated as manual-review cases for compatibility
 * scoring, because the converter has not been taught their GeoEvent-to-Arcade
 * semantics.
 */
const TRAINED_ARCADE_FUNCTIONS = new Set([
  'abs',
  'ceil',
  'concatenate',
  'count',
  'find',
  'floor',
  'hasvalue',
  'includes',
  'isempty',
  'left',
  'log',
  'lower',
  'max',
  'mid',
  'min',
  'now',
  'number',
  'pow',
  'replace',
  'right',
  'round',
  'sqrt',
  'text',
  'trim',
  'upper',
]);

/**
 * Scans an Arcade expression for function calls that are unsupported
 * in the Velocity Arcade profile. Returns the list of unsupported
 * function names found (case-insensitive matching).
 */
export function findUnsupportedArcadeFunctions(arcade: string): string[] {
  const found: string[] = [];
  for (const fn of ARCADE_UNSUPPORTED_FUNCTIONS) {
    const pattern = new RegExp(`\\b${fn}\\s*\\(`, 'i');
    if (pattern.test(arcade)) {
      found.push(fn);
    }
  }
  return found;
}

export function stripAutoConvertedArcadeComment(expression: string): string {
  const verifyComment = expression.match(/^\/\* VERIFY:[\s\S]*?\*\/\s*([\s\S]*)$/);
  return verifyComment?.[1]?.trim() || expression;
}

function stripArcadeComments(expression: string): string {
  return expression.replace(/\/\*[\s\S]*?\*\//g, '').replace(/\/\/.*$/gm, '');
}

function stripStringLiterals(expression: string): string {
  return expression
    .replace(/"(?:\\.|[^"\\])*"/g, '""')
    .replace(/'(?:\\.|[^'\\])*'/g, "''");
}

function findUntrainedFunctionCalls(arcade: string): string[] {
  const body = stripStringLiterals(stripArcadeComments(arcade));
  const found = new Set<string>();
  const functionPattern = /\b([A-Za-z_][A-Za-z0-9_]*)\s*\(/g;
  let match: RegExpExecArray | null;
  while ((match = functionPattern.exec(body)) !== null) {
    const name = match[1] ?? '';
    const lower = name.toLowerCase();
    if (!TRAINED_ARCADE_FUNCTIONS.has(lower)) {
      found.add(name);
    }
  }
  return [...found];
}

/**
 * Attempts to convert a GeoEvent processor expression string to Arcade.
 * Handles simple field references and common patterns. Falls back to an
 * action-required comment preserving the original expression for manual conversion.
 *
 * Velocity Arcade profile supports: $feature, $target, $join, $analytic variables.
 * Available function bundles: Core (Array, Date, Dictionary, Feature, Logical,
 * Math, Text), Geometry, and Track.
 * See: https://developers.arcgis.com/arcade/profiles/velocity/
 */
export function convertGeoEventExpressionToArcade(expr: string, knownFields: string[] = []): string {
  if (!expr || expr.trim() === '') {
    return '/* ACTION REQUIRED: No GeoEvent expression provided - add Arcade expression manually.\n' +
      '   Arcade reference: https://developers.arcgis.com/arcade/profiles/velocity/\n' +
      '   Velocity Arcade guide: https://doc.arcgis.com/en/iot/analyze/use-arcade-expressions.htm */ null';
  }

  const stringLiteralMatch = expr.match(/^'([^']*)'$/);
  if (stringLiteralMatch) {
    return JSON.stringify(stringLiteralMatch[1]);
  }

  if (/^-?\d+(\.\d+)?$/.test(expr.trim())) {
    return expr.trim();
  }

  let arcade = expr;

  arcade = arcade.replace(/\$\{([^}]+)\}/g, '$feature["$1"]');

  const inLhs = '(\\$feature\\["[^"]+"\\]|\\$feature\\.[A-Za-z_][\\w.]*|[A-Za-z_][\\w.]*)';
  const inListPattern = new RegExp(`${inLhs}\\s+(NOT\\s+)?IN\\s*\\(([^)]*)\\)`, 'gi');
  arcade = arcade.replace(inListPattern, (_match, lhs: string, notKw: string | undefined, listBody: string) => {
    const negate = !!notKw;
    const items = listBody
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((raw) => {
        const sq = raw.match(/^'(.*)'$/);
        if (sq) return JSON.stringify(sq[1]);
        const dq = raw.match(/^"(.*)"$/);
        if (dq) return JSON.stringify(dq[1]);
        return raw;
      });
    if (items.length === 0) {
      return negate ? `${lhs} != null` : `${lhs} == null`;
    }
    if (items.length === 1) {
      return `${lhs} ${negate ? '!=' : '=='} ${items[0]}`;
    }
    const arr = `[${items.join(', ')}]`;
    return `${negate ? '!' : ''}Includes(${arr}, ${lhs})`;
  });

  const nullLhs = '(\\$feature\\["[^"]+"\\]|\\$feature\\.[A-Za-z_][\\w.]*|[A-Za-z_][\\w.]*)';
  arcade = arcade.replace(
    new RegExp(`${nullLhs}\\s+(NOT\\s+)?EXISTS\\b`, 'gi'),
    (_m, lhs: string, notKw: string | undefined) => `${notKw ? '' : '!'}IsEmpty(${lhs})`,
  );
  arcade = arcade.replace(
    new RegExp(`${nullLhs}\\s+IS\\s+NOT\\s+NULL\\b`, 'gi'),
    (_m, lhs: string) => `!IsEmpty(${lhs})`,
  );
  arcade = arcade.replace(
    new RegExp(`${nullLhs}\\s+IS\\s+NULL\\b`, 'gi'),
    (_m, lhs: string) => `IsEmpty(${lhs})`,
  );
  arcade = arcade.replace(
    new RegExp(`${nullLhs}\\s+NOTNULL\\b`, 'gi'),
    (_m, lhs: string) => `${lhs} != null`,
  );
  arcade = arcade.replace(
    new RegExp(`${nullLhs}\\s+ISNULL\\b`, 'gi'),
    (_m, lhs: string) => `${lhs} == null`,
  );

  arcade = arcade.replace(/\bAND\b/gi, '&&');
  arcade = arcade.replace(/\bOR\b/gi, '||');
  arcade = arcade.replace(/\bNOT\b/gi, '!');
  arcade = arcade.replace(/(?<![!=<>])=(?!=)/g, '==');

  arcade = arcade.replace(/\btoString\b/gi, 'Text');
  arcade = arcade.replace(/\btoInteger\b/gi, 'Number');
  arcade = arcade.replace(/\btoDouble\b/gi, 'Number');
  arcade = arcade.replace(/\btoLong\b/gi, 'Number');
  arcade = arcade.replace(/\btoFloat\b/gi, 'Number');

  arcade = arcade.replace(/\bconcat\b/gi, 'Concatenate');
  arcade = arcade.replace(/\bsubstring\b/gi, 'Mid');
  arcade = arcade.replace(/\btoUpper\b/gi, 'Upper');
  arcade = arcade.replace(/\btoLower\b/gi, 'Lower');
  arcade = arcade.replace(/\btrim\b/gi, 'Trim');
  arcade = arcade.replace(/\bindexOf\b/gi, 'Find');
  arcade = arcade.replace(/\breplaceAll\b/gi, 'Replace');
  arcade = arcade.replace(/\bstartsWith\b/gi, 'Left');
  arcade = arcade.replace(/\bendsWith\b/gi, 'Right');

  arcade = arcade.replace(/\babs\b/gi, 'Abs');
  arcade = arcade.replace(/\bceil\b/gi, 'Ceil');
  arcade = arcade.replace(/\bfloor\b/gi, 'Floor');
  arcade = arcade.replace(/\bround\b/gi, 'Round');
  arcade = arcade.replace(/\bsqrt\b/gi, 'Sqrt');
  arcade = arcade.replace(/\blog\b/gi, 'Log');
  arcade = arcade.replace(/\bpow\b/gi, 'Pow');
  arcade = arcade.replace(/\bmin\b/gi, 'Min');
  arcade = arcade.replace(/\bmax\b/gi, 'Max');

  arcade = arcade.replace(/\bhasField\b/gi, 'HasValue');
  arcade = arcade.replace(/\bisNull\b/gi, 'IsEmpty');

  arcade = arcade.replace(/\bnow\(\)/gi, 'Now()');
  arcade = arcade.replace(/\bcurrentTime\(\)/gi, 'Now()');
  arcade = arcade.replace(/\bgetTimestamp\(\)/gi, 'Now()');

  arcade = arcade.replace(/\blength\b/gi, 'Count');

  if (knownFields.length > 0) {
    const fieldByLower = new Map<string, string>();
    for (const f of knownFields) {
      if (f && !fieldByLower.has(f.toLowerCase())) fieldByLower.set(f.toLowerCase(), f);
    }
    const sortedNames = [...fieldByLower.keys()].sort((a, b) => b.length - a.length);
    if (sortedNames.length > 0) {
      const escapedAlt = sortedNames
        .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|');
      const bareRe = new RegExp(
        `(^|[^\\w.\\["'])(${escapedAlt})(?![\\w(])`,
        'gi',
      );
      const parts = arcade.split(/('[^']*')/g);
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (part === undefined || part.startsWith("'")) continue;
        parts[i] = part.replace(bareRe, (_m, pre: string, ident: string) => {
          const resolved = fieldByLower.get(ident.toLowerCase()) ?? ident;
          return `${pre}$feature.${resolved}`;
        });
      }
      arcade = parts.join('');
    }
  }

  if (
    /'[^']*'/.test(arcade) &&
    /\+/.test(arcade) &&
    !/[<>=!*/%-]/.test(arcade.replace(/'[^']*'/g, ''))
  ) {
    const operands = arcade.split('+').map((s) => s.trim()).filter((s) => s.length > 0);
    const normalized = operands.map((op) => {
      const sq = op.match(/^'(.*)'$/);
      return sq ? JSON.stringify(sq[1]) : op;
    });
    arcade = `Concatenate([${normalized.join(', ')}], "")`;
  }

  if (arcade.includes('$feature[') || arcade.includes('$feature.')) {
    return `/* VERIFY: Auto-converted from GeoEvent - review for accuracy.\n` +
      `   Null-handling tip: use $feature["field"] != null && <condition> for numeric comparisons.\n` +
      `   Arcade reference: https://developers.arcgis.com/arcade/profiles/velocity/ */\n${arcade}`;
  }

  return `/* ACTION REQUIRED: Manually convert GeoEvent expression to Arcade: ${expr}\n` +
    `   Arcade reference: https://developers.arcgis.com/arcade/profiles/velocity/\n` +
    `   Velocity Arcade guide: https://doc.arcgis.com/en/iot/analyze/use-arcade-expressions.htm */ null`;
}

export function assessGeoEventExpressionArcadeConversion(
  expr: string,
  knownFields: string[] = []
): ArcadeConversionAssessment {
  const expression = convertGeoEventExpressionToArcade(expr, knownFields);
  const unsupportedFunctions = findUnsupportedArcadeFunctions(expression);
  const untrainedFunctions = findUntrainedFunctionCalls(expression);

  if (expression.includes('ACTION REQUIRED')) {
    return {
      expression,
      autoConverted: false,
      reason: 'manual conversion required',
      unsupportedFunctions,
      untrainedFunctions,
    };
  }

  if (unsupportedFunctions.length > 0) {
    return {
      expression,
      autoConverted: false,
      reason: `unsupported Velocity Arcade function(s): ${unsupportedFunctions.join(', ')}`,
      unsupportedFunctions,
      untrainedFunctions,
    };
  }

  if (untrainedFunctions.length > 0) {
    return {
      expression,
      autoConverted: false,
      reason: `untrained function conversion(s): ${untrainedFunctions.join(', ')}`,
      unsupportedFunctions,
      untrainedFunctions,
    };
  }

  return {
    expression,
    autoConverted: true,
    unsupportedFunctions,
    untrainedFunctions,
  };
}
